import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiGet, apiPost } from "@/utils/api";

const RATE_STANDARD = "1"; const RATE_SPECIAL = "3";

export const MaterialGstSetupStep = ({ materials, onContinue, onBack }) => {
  const [rows, setRows] = useState(null); // { [code]: { description, hsn, selected, current, applying, error } }
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const codes = materials.map((m) => m.material_code).join(",");
    if (!codes) { setRows({}); return; }
    apiGet(`/tax-settings/materials?codes=${encodeURIComponent(codes)}`)
      .then(({ materials: byCode }) => {
        const next = {};
        materials.forEach((m) => {
          const status = byCode[m.material_code] || {};
          next[m.material_code] = { description: m.material_description, hsn: m.hsn_code, found: status.found !== false, selected: status.rate_type || RATE_STANDARD, current: status.rate_type || RATE_STANDARD, rawCurrent: status.rate_type || null, applying: false, applied: false, error: "" };
        });
        setRows(next);
      })
      .catch((err) => setLoadError(err.message));
  }, [materials]);

  const setSelected = (code, value) => setRows((current) => ({ ...current, [code]: { ...current[code], selected: value, applied: false } }));

  const applyRow = (code, rateType) => {
    setRows((current) => ({ ...current, [code]: { ...current[code], applying: true, error: "" } }));
    apiPost(`/tax-settings/material/${encodeURIComponent(code)}/apply?rate_type=${rateType}`, {})
      .then((updated) => setRows((current) => ({ ...current, [code]: { ...current[code], applying: false, applied: true, current: updated.rate_type, rawCurrent: updated.rate_type } })))
      .catch((err) => setRows((current) => ({ ...current, [code]: { ...current[code], applying: false, error: err.message } })));
  };

  if (loadError) return <div role="alert" className="border-l-2 border-red-600 bg-red-50 p-4 text-sm text-red-900" data-testid="gst-setup-error">{loadError}</div>;
  if (!rows) return <div className="flex min-h-40 items-center justify-center border border-slate-200 bg-white" data-testid="gst-setup-loading"><Loader2 size={18} className="animate-spin text-[#076D74]" /><span className="ml-2 text-sm text-slate-600">Checking SAP GST classification for each material…</span></div>;

  return <section className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_2fr]" data-testid="gst-setup-step">
    <div className="border border-slate-200 bg-white p-5">
      <p className="font-mono text-xs font-semibold uppercase text-[#076D74]">Step 2 of 2 · Material setting</p>
      <h2 className="mt-1 text-xl font-heading font-semibold text-slate-950">GST slab per material</h2>
      <p className="mt-3 text-sm text-slate-600">Standard is 18% GST (CGST+SGST or IGST). Special 1 is the 5% GST Council rate rationalization slab. Only change materials you know moved slabs — this writes straight to the SAP Material Master.</p>
    </div>
    <div className="border border-slate-200 bg-white p-4">
      <p className="mb-2 text-xs text-slate-400 lg:hidden" data-testid="gst-setup-scroll-hint">Scroll right to see all columns →</p>
      <div className="overflow-x-auto">
      <Table data-testid="gst-setup-table">
        <TableHeader><TableRow><TableHead>Material</TableHead><TableHead>HSN</TableHead><TableHead>Current</TableHead><TableHead>Set to</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {Object.entries(rows).map(([code, row]) => <TableRow key={code} data-testid={`gst-setup-row-${code}`}>
            <TableCell><div className="font-medium text-slate-900">{code}</div><div className="text-xs text-slate-500">{row.description}</div></TableCell>
            <TableCell className="text-xs text-slate-700" data-testid={`gst-setup-hsn-${code}`}>{row.hsn || "—"}</TableCell>
            {!row.found
              ? <TableCell colSpan={3} className="text-xs text-red-700" data-testid={`gst-setup-not-found-${code}`}>Not found in SAP Material Master — skip, nothing to set</TableCell>
              : <>
                <TableCell><span className={`text-xs font-semibold ${row.rawCurrent === RATE_SPECIAL ? "text-emerald-700" : "text-slate-600"}`} data-testid={`gst-setup-current-${code}`}>{row.rawCurrent === RATE_SPECIAL ? "Special (5%)" : row.rawCurrent === RATE_STANDARD ? "Standard (18%)" : "Not set (defaults to 18%)"}</span></TableCell>
                <TableCell>
                  <div className="flex gap-1" role="radiogroup" aria-label={`GST rate for ${code}`}>
                    <button type="button" onClick={() => setSelected(code, RATE_STANDARD)} className={`border px-2 py-1 text-xs ${row.selected === RATE_STANDARD ? "border-[#0A6755] bg-[#0A6755] text-white" : "border-slate-300 bg-white text-slate-700"}`} data-testid={`gst-setup-standard-${code}`}>Standard 18%</button>
                    <button type="button" onClick={() => setSelected(code, RATE_SPECIAL)} className={`border px-2 py-1 text-xs ${row.selected === RATE_SPECIAL ? "border-[#0A6755] bg-[#0A6755] text-white" : "border-slate-300 bg-white text-slate-700"}`} data-testid={`gst-setup-special-${code}`}>Special 5%</button>
                  </div>
                  {row.error && <p className="mt-1 text-xs text-red-700" data-testid={`gst-setup-row-error-${code}`}>{row.error}</p>}
                </TableCell>
                <TableCell>
                  {row.applied || row.selected === row.current
                    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid={`gst-setup-confirmed-${code}`}><CheckCircle2 size={14} />Confirmed</span>
                    : <Button size="sm" variant="outline" onClick={() => applyRow(code, row.selected)} disabled={row.applying} className="gap-1" data-testid={`gst-setup-apply-${code}`}>{row.applying ? <Loader2 size={13} className="animate-spin" /> : null}Apply</Button>}
                </TableCell>
              </>}
          </TableRow>)}
        </TableBody>
      </Table>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        {onBack && <Button variant="ghost" onClick={onBack} data-testid="gst-setup-back-button">Back</Button>}
        <Button onClick={onContinue} className="gap-2 bg-[#0A6755] hover:bg-[#076D74]" data-testid="gst-setup-continue-button">Continue to invoice</Button>
      </div>
    </div>
  </section>;
};
